// ═════════════════════════════════════════════════════════════════════════════
// Server connectors graph → the WIG Connections canvas's own shape.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES.
//
//   10x-coach-frontend/src/components/CoachPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/connectorsGraph.js
//   10x-frontend/src/components/UserPanel/ExecutionBlueprint/
//     ExecutionBlueprintLiveReview/_shared/connectorsGraph.js
//
// `diff` is the test; change one copy, change both in the same pass. And here the
// reason bites harder than usual: this file decides **which band a WIG sits in,
// what code it shows, and which end of an arrow points where.** If the two apps
// disagree about any of that, the coach and the CEO are looking at two different
// maps of the same blueprint while talking to each other about it.
//
// Kept dependency-free for the same reason as `liveReviewAgenda.js` — nothing
// here can resolve differently in the two apps.
//
// ── Why an adapter instead of changing either side ──────────────────────────
// The canvas (`WigConnectionsCanvas.js`) was built from Figma against a
// hand-written mock and its geometry has been tuned over twelve passes —
// arrowhead trim, fan offsets, detour arcs, the z-index ladder. The server's
// §6a graph is a different shape: a BHAG object plus flat items plus edges with
// node-type discriminators. Rewriting the canvas to the server's shape would put
// all of that tuned geometry at risk for no benefit, and rewriting the server's
// shape is not ours to do. So this translates, once, in one place, and the
// canvas keeps taking the props it always took.
//
// ── The two id conventions, and the one rule that matters ───────────────────
// The canvas identifies a node by an opaque string id. The server identifies an
// edge endpoint by a `{ *_node_type, *_item_id }` PAIR, where the item id is
// null exactly when the node type is `"bhag"`.
//
// So: **`BHAG_NODE_ID` is the canvas-side name for the BHAG root, and
// `toEndpoint` is the ONLY place that converts back.** Never build a
// `{ nodeType, itemId }` pair by hand at a call site — that is how an item id
// ends up in a bhag endpoint, which the server rejects as `invalid_connector`
// and which is tedious to trace back from.
// ═════════════════════════════════════════════════════════════════════════════

// The canvas's own id for the BHAG. Deliberately not a number and not something
// an `execution_blueprint_item_id` could ever collide with.
export const BHAG_NODE_ID = "bhag";

// The canvas's stroke vocabulary. These are the values `WigConnectionsCanvas`
// compares against to decide how to draw a line, so they are the canvas's
// language, NOT the server's — the server says `ai_suggested`, this says `ai`.
// `mapSource` below is the boundary between the two.
export const CONNECTION_SOURCE = {
  CONFIRMED: "confirmed",
  AI: "ai",
  USER: "user",
};

// The server's `source` values (§6a). Only `ai_suggested` is documented; a
// human-drawn connector's value is not, so anything that is NOT explicitly
// `ai_suggested` is treated as settled. That direction is the safe one: showing
// an agreed connection as unreviewed nags about work already done, whereas the
// reverse would quietly hide work still to do.
export const mapSource = (serverSource) =>
  serverSource === "ai_suggested"
    ? CONNECTION_SOURCE.AI
    : CONNECTION_SOURCE.CONFIRMED;

// Section key → the short base used for the band tone, the icon filename and
// the WIG code letter. Derived from the key rather than the label because the
// label is display copy the backend may reword ("Financial WIG" → "Financial
// Objectives") while `section_key` is a fixed enum (08-fe-handoff §1).
const SECTION_BASE = {
  financial_wig: "financial",
  customer_wig: "customer",
  operations_wig: "operations",
  people_wig: "people",
};

// Falls back to the key's own first word so an unrecognised fifth section still
// renders with a readable band rather than disappearing. It will have no icon
// and an unstyled tone, which is the visible-degradation we want over a silent
// drop — a WIG missing from the map is far worse than an unstyled one.
const baseFor = (sectionKey) =>
  SECTION_BASE[sectionKey] || String(sectionKey || "").split("_")[0] || "wig";

// The band icons live in each app's own `public/` folder, so the path is a
// runtime URL rather than an import — which is also what keeps this file
// import-free and therefore shareable. ⚠️ Both apps must carry the same five
// files at this path; the CEO app's copies were added alongside this module.
const ICON_BASE = "/assets/images/execution-blueprint";
const iconFor = (base) => `${ICON_BASE}/${base}.svg`;

/**
 * Convert a canvas node id back into a server edge endpoint.
 * The only sanctioned way to build one — see the header.
 */
export const toEndpoint = (nodeId) =>
  String(nodeId) === BHAG_NODE_ID
    ? { nodeType: "bhag" }
    : { nodeType: "item", itemId: Number(nodeId) };

/** The canvas node id for a server edge endpoint. */
export const nodeIdFor = (nodeType, itemId) =>
  nodeType === "bhag" ? BHAG_NODE_ID : String(itemId);

/**
 * Build everything the canvas needs from the live snapshot.
 *
 * @param connectors The normalised §6a block — `{ nodes, edges, overview }`.
 * @param sections   The snapshot's `sections`, used ONLY for band order, band
 *                   labels and per-band item order. The graph's own
 *                   `nodes.items` carry a `section_key` but no ordering, and the
 *                   two lists must not disagree about which band a WIG is in —
 *                   so the sections tree wins, being what the rest of the
 *                   session displayed.
 * @returns `{ bands, nodes, connections, hasGraph }`
 */
export const buildConnectorGraph = (connectors, sections) => {
  const orderedSections = [...(sections || [])].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
  );

  // BHAG heads the map, then the four WIG sections in the session's own order.
  const bands = [
    { key: BHAG_NODE_ID, label: "BHAG", tone: "bhag" },
    ...orderedSections.map((section) => ({
      key: section.section_key,
      // Uppercased because the band rail is set in caps in the design, and doing
      // it here rather than in CSS keeps the canvas free of copy decisions.
      label: String(
        section.label || baseFor(section.section_key),
      ).toUpperCase(),
      tone: baseFor(section.section_key),
    })),
  ];

  const graphItems = connectors?.nodes?.items || [];
  const bySectionKey = {};
  graphItems.forEach((item) => {
    const key = String(item.execution_blueprint_item_id);
    bySectionKey[key] = item;
  });

  const nodes = [];

  // The BHAG node. Present whenever the graph names one — and the graph is the
  // only place a BHAG label exists on the coach side at all (it is in no other
  // coach payload), which is worth remembering before "tidying" this to read
  // from somewhere more obvious.
  const bhag = connectors?.nodes?.bhag;
  if (bhag) {
    nodes.push({
      id: BHAG_NODE_ID,
      band: BHAG_NODE_ID,
      code: "BHAG",
      label: bhag.label || "",
      icon: iconFor("bhag"),
    });
  }

  // Then each section's items, in the section's own item order, numbered within
  // the band. The CODE (F1, C2, …) is DERIVED — the server sends no such field.
  // It is display shorthand the design shows on every card, and it is derived
  // from position, so it is stable for a given payload but NOT an identifier:
  // never persist it, never match on it, never send it anywhere.
  orderedSections.forEach((section) => {
    const base = baseFor(section.section_key);
    const letter = base.charAt(0).toUpperCase();
    let n = 0;
    (section.items || []).forEach((item) => {
      const key = String(item.execution_blueprint_item_id);
      const fromGraph = bySectionKey[key];
      // Only items the GRAPH knows about are placed. An item the graph omits is
      // not a WIG in this map — drawing it would offer a connection the server
      // would refuse with `invalid_connector` ("an item not on this blueprint").
      if (!fromGraph) return;
      n += 1;
      nodes.push({
        id: key,
        band: section.section_key,
        code: `${letter}${n}`,
        // The sections tree's label wins over the graph's copy of it: an item
        // edited during the finalize step updated there, and the graph's label
        // is a snapshot from when the agent ran.
        label: item.label || fromGraph.label || "",
        // The WIG's actual wording, carried alongside its label so the card can
        // show both (2026-08-31, on instruction). `label` is the short name
        // ("Revenue Target"); `value` is what the goal actually says. The map
        // was showing only the former, which reads as a list of headings.
        //
        // Same precedence as `label`: the sections tree wins over the graph's
        // snapshot, because an item edited during the finalize step updated
        // there. Empty string rather than null so the card can test truthiness
        // without a guard.
        value: item.value || fromGraph.value || "",
        icon: iconFor(base),
      });
      delete bySectionKey[key];
    });
  });

  // Anything the graph has that the sections tree doesn't gets appended to its
  // own section's band if we can name one, rather than being dropped — same
  // reasoning as above in reverse: a connectable node that can't be seen is a
  // connection the user can't make.
  Object.keys(bySectionKey).forEach((key) => {
    const item = bySectionKey[key];
    const base = baseFor(item.section_key);
    nodes.push({
      id: key,
      band: item.section_key,
      code: "—",
      label: item.label || "",
      value: item.value || "",
      icon: iconFor(base),
    });
  });

  const connections = (connectors?.edges || []).map((edge) => ({
    id: edge.id,
    from: nodeIdFor(edge.from_node_type, edge.from_item_id),
    to: nodeIdFor(edge.to_node_type, edge.to_item_id),
    source: mapSource(edge.source),
  }));

  return {
    bands,
    nodes,
    connections,
    // Distinguishes "the agent produced nothing" (a real answer — §6a says a
    // twice-failed agent still sends the block, empty) from "we have no block
    // yet". The canvas renders an empty map for the first and shouldn't be
    // mounted at all for the second.
    hasGraph: !!connectors,
  };
};
